// app/auth/signin/page.tsx
'use client'

export const dynamic = 'force-dynamic'

import { useEffect, useRef, useState, Suspense } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { useAuth } from '@/lib/auth/auth-context'
import {
  invitedAddress,
  invitePointerIn,
  loginWithGoogle,
  loginWithMicrosoft,
  requestSignInCode,
  resolveSignIn,
  verifySignInCode,
  type SignInMethod,
} from '@/lib/auth/jwt-client'
import { lastSignInMethod, rememberSignInMethod } from '@/lib/last-sign-in-method'
import { postAuthDestinationCookie } from '@/lib/auth/post-auth-destination'
import { AuthBrandPanel } from '@/components/auth-brand-panel'
import { cn } from '@/lib/utils'

/**
 * What the brand half argues for. Ordered to match the lifecycle a buyer walks:
 * issue work, compare responses, then live with the contract.
 */
const PROOF_POINTS = [
  'Issue work requests to your private marketplace',
  'Receive and compare vendor proposals side by side',
  'Track contracts and obligations once work is awarded',
]

const FIELD =
  'w-full rounded-lg border border-ui-border bg-white px-3.5 py-2.5 text-[14px] text-slate-900 placeholder:text-slate-400 outline-none transition-shadow focus:border-brand focus:ring-2 focus:ring-brand/20'

const OAUTH_BUTTON =
  'flex items-center justify-center gap-2.5 rounded-lg border border-ui-border bg-white px-4 py-2.5 text-[13px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40'

const PRIMARY_BUTTON =
  'flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-brand-dark disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2'

function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}

function MicrosoftIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#F25022" d="M0 0h11.377v11.372H0V0z" />
      <path fill="#7FBA00" d="M12.623 0H24v11.372H12.623V0z" />
      <path fill="#00A4EF" d="M0 12.623h11.377V24H0V12.623z" />
      <path fill="#FFB900" d="M12.623 12.623H24V24H12.623V12.623z" />
    </svg>
  )
}

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  )
}

function LastUsed() {
  return <span className="ml-2 text-[11px] font-medium text-slate-400">Last used</span>
}

type Step = 'email' | 'methods' | 'code'

function SignInContent() {
  const [step, setStep] = useState<Step>('email')
  const [methods, setMethods] = useState<SignInMethod[]>([])
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [notice, setNotice] = useState('')
  const [loading, setLoading] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()
  const [error, setError] = useState(searchParams.get('error') ?? '')
  const message = searchParams.get('message')
  const callbackUrl = searchParams.get('callbackUrl') || '/'
  // An invitation's link points to the address it was sent to, so an invitee is not asked for it.
  const invitePointer = invitePointerIn(searchParams, callbackUrl)
  const [arriving, setArriving] = useState(!!invitePointer)
  const arrived = useRef(false)
  const { login, refreshUser } = useAuth()

  const remembered = typeof window === 'undefined' ? null : lastSignInMethod(window.localStorage)
  // A password is behind a link, open from the start for someone whose last sign-in used one.
  const [passwordOpened, setPasswordOpened] = useState(false)
  const passwordOpen = passwordOpened || remembered === 'password'

  const attempt = async (action: () => Promise<void>) => {
    setLoading(true)
    setError('')
    try {
      await action()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleEmail = (e: React.FormEvent) => {
    e.preventDefault()
    return attempt(async () => {
      const branch = await resolveSignIn(email)
      if (branch.branch === 'redirect') {
        handleOAuth(branch.provider)
        return
      }
      setMethods(branch.methods)
      setStep('methods')
    })
  }

  const handlePassword = (e: React.FormEvent) => {
    e.preventDefault()
    return attempt(async () => {
      await login({ email, password })
      rememberSignInMethod(window.localStorage, 'password')
      router.push(callbackUrl)
    })
  }

  const handleOAuth = (provider: 'google' | 'microsoft', address = email) => {
    rememberSignInMethod(window.localStorage, provider)
    // The provider returns to /auth/callback, which knows nothing of where this sign-in began.
    document.cookie = postAuthDestinationCookie(callbackUrl)
    if (provider === 'google') loginWithGoogle(address)
    else loginWithMicrosoft(address)
  }

  // Arriving from an invitation: the address is filled in and the page carries on exactly as if it
  // had been typed. An organisation that requires its provider goes straight there; anyone else
  // chooses how to sign in. Once only, so StrictMode's second run asks nothing twice.
  useEffect(() => {
    if (!invitePointer || arrived.current) return
    arrived.current = true
    void (async () => {
      const address = await invitedAddress(invitePointer)
      if (address) {
        setEmail(address)
        try {
          const branch = await resolveSignIn(address)
          if (branch.branch === 'redirect') {
            handleOAuth(branch.provider, address)
            return
          }
          setMethods(branch.methods)
          setStep('methods')
        } catch {
          // The address stays filled in, and the email step asks again.
        }
      }
      setArriving(false)
    })()
    // Asked once for the pointer the page arrived with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitePointer])

  const handleSendCode = () =>
    attempt(async () => {
      await requestSignInCode(email)
      // The server answers the same whether or not a code went out, so it never says which
      // addresses it knows: a code goes only to an account or an invitation.
      setNotice(`If ${email} has a Partnr account or an invitation, a code is on its way.`)
      setStep('code')
    })

  const handleCode = (e: React.FormEvent) => {
    e.preventDefault()
    return attempt(async () => {
      await verifySignInCode(email, code)
      rememberSignInMethod(window.localStorage, 'code')
      await refreshUser()
      router.push(callbackUrl)
    })
  }

  const startOver = () => {
    setStep('email')
    setMethods([])
    setPassword('')
    setPasswordOpened(false)
    setCode('')
    setNotice('')
    setError('')
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <AuthBrandPanel
        headline="Procurement, from first brief to final obligation."
        points={PROOF_POINTS}
      />

      {/* Form half */}
      <div className="flex items-center justify-center px-6 py-12 lg:px-14">
        <div className="w-full max-w-[380px]">
          <h1 className="text-[28px] font-bold tracking-tight text-slate-900">Sign in</h1>
          <p className="mt-2 text-[14px] text-slate-500">
            {step === 'email'
              ? 'Enter your work email address to continue.'
              : 'Welcome back. Choose how you want to continue.'}
          </p>

          {message && (
            <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-3 text-[13px] text-emerald-800">
              {message}
            </div>
          )}

          {notice && (
            <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-3 text-[13px] text-slate-700">
              {notice}
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="mt-6 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-[13px] text-red-700"
            >
              {error}
            </div>
          )}

          {step === 'email' && arriving && (
            <p className="mt-7 text-[13px] text-slate-500">Opening your invitation…</p>
          )}

          {step === 'email' && !arriving && (
            <form onSubmit={handleEmail} className="mt-7 space-y-4">
              <div>
                <label htmlFor="email" className="mb-1.5 block text-[13px] font-semibold text-slate-700">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={FIELD}
                  placeholder="you@company.com"
                  autoComplete="email"
                  autoFocus
                  required
                />
              </div>

              <button type="submit" disabled={loading} className={PRIMARY_BUTTON}>
                {loading && <Spinner />}
                {loading ? 'Checking…' : 'Continue'}
              </button>
            </form>
          )}

          {step !== 'email' && (
            <div className="mt-7 flex items-center justify-between rounded-lg border border-ui-border bg-slate-50 px-3.5 py-2.5">
              <span className="truncate text-[13px] text-slate-700">{email}</span>
              <button
                type="button"
                onClick={startOver}
                className="ml-3 shrink-0 text-[13px] font-semibold text-brand hover:underline"
              >
                Change
              </button>
            </div>
          )}

          {step === 'methods' && (
            <>
              {methods.includes('code') && (
                <button
                  type="button"
                  onClick={handleSendCode}
                  disabled={loading}
                  className={cn('mt-4', passwordOpen ? `w-full ${OAUTH_BUTTON}` : PRIMARY_BUTTON)}
                >
                  {loading && !passwordOpen && <Spinner />}
                  Email me a sign-in code
                  {remembered === 'code' && <LastUsed />}
                </button>
              )}

              {methods.includes('password') && !passwordOpen && (
                <button
                  type="button"
                  onClick={() => setPasswordOpened(true)}
                  className="mt-3 text-[13px] font-semibold text-brand hover:underline"
                >
                  Enter a password
                </button>
              )}

              {methods.includes('password') && passwordOpen && (
                <form onSubmit={handlePassword} className="mt-4 space-y-4">
                  <div>
                    <label htmlFor="password" className="mb-1.5 block text-[13px] font-semibold text-slate-700">
                      Password
                      {remembered === 'password' && <LastUsed />}
                    </label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={FIELD}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      autoFocus
                      required
                    />
                  </div>

                  <button type="submit" disabled={loading} className={PRIMARY_BUTTON}>
                    {loading && <Spinner />}
                    {loading ? 'Signing in…' : 'Sign in'}
                  </button>
                </form>
              )}

              {(methods.includes('google') || methods.includes('microsoft')) && (
                <div className="my-6 flex items-center gap-4">
                  <span className="h-px flex-1 bg-ui-border" />
                  <span className="text-[12px] text-slate-400">or continue with</span>
                  <span className="h-px flex-1 bg-ui-border" />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                {methods.includes('google') && (
                  <button type="button" onClick={() => handleOAuth('google')} className={OAUTH_BUTTON}>
                    <GoogleIcon />
                    Google
                    {remembered === 'google' && <LastUsed />}
                  </button>
                )}
                {methods.includes('microsoft') && (
                  <button type="button" onClick={() => handleOAuth('microsoft')} className={OAUTH_BUTTON}>
                    <MicrosoftIcon />
                    Microsoft
                    {remembered === 'microsoft' && <LastUsed />}
                  </button>
                )}
              </div>
            </>
          )}

          {step === 'code' && (
            <form onSubmit={handleCode} className="mt-4 space-y-4">
              <div>
                <label htmlFor="code" className="mb-1.5 block text-[13px] font-semibold text-slate-700">
                  Sign-in code
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className={FIELD}
                  placeholder="123456"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                />
              </div>

              <button type="submit" disabled={loading} className={PRIMARY_BUTTON}>
                {loading && <Spinner />}
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          )}

          <p className="mt-8 text-[14px] text-slate-500">
            Don&apos;t have an account?{' '}
            <Link href="/auth/signup" className="font-semibold text-brand hover:underline">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default function SignIn() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-white lg:grid lg:grid-cols-2"><div className="hidden h-full bg-brand lg:block" /></div>}>
      <SignInContent />
    </Suspense>
  )
}
